import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSiteInfo } from '../../../context/SiteInfoContext';
import ImageGallery from './ImageGallery';
import VehicleSpecs from './VehicleSpecs';
import FinanceCalculator from './FinanceCalculator';
import EnquiryForm from './EnquiryForm';
import SimilarVehicles from './SimilarVehicles';
import { IconStar, IconPhone } from '../../ui/Icons';
import { getVehicleById } from '../../../data/vehicles';
import { useVehicles } from '../../../context/VehiclesContext';
import './VehicleDetails.css';

const VehicleDetailsPage = ({ onViewDetails }) => {
  const { siteInfo } = useSiteInfo();
  const { vehicles: allVehicles } = useVehicles();
  const { stockId } = useParams();
  const vehicle = getVehicleById(allVehicles, stockId) || allVehicles[0];

  useEffect(() => {
    if (vehicle) {
      document.title = `${vehicle.year ? vehicle.year + ' ' : ''}${vehicle.title}${vehicle.subtitle ? ' ' + vehicle.subtitle : ''} | JAC Cars`;
    }
  }, [vehicle]);

  if (!vehicle) {
    return (
      <div className="vehicle-details-page">
        <div className="container">
          <p>No vehicle data available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vehicle-details-page">
      <div className="container">
        <nav className="breadcrumb">
          <a href="/">Home</a>
          <span>/</span>
          <a href="/used-cars">Used Cars</a>
          <span>/</span>
          <span>{vehicle.title}</span>
        </nav>

        <div className="vehicle-header-mobile">
          <h1>{vehicle.title}</h1>
          {vehicle.subtitle && <p className="vehicle-subtitle">{vehicle.subtitle}</p>}
          <div className="price-container">
            <span className="current-price">£{vehicle.price.toLocaleString()}</span>
            {vehicle.wasPrice && (
              <span className="was-price">was £{vehicle.wasPrice.toLocaleString()}</span>
            )}
          </div>
        </div>

        <div className="vehicle-gallery-full">
          <ImageGallery images={vehicle.images} vehicleName={vehicle.title} />
        </div>

        <div className="vehicle-content-grid">
          <div className="vehicle-main">
            <div className="vehicle-actions-mobile">
              <button className="btn btn-primary">Reserve Online £99</button>
              <button className="btn btn-secondary">Book Test Drive</button>
            </div>

            <div className="vehicle-description-section">
              <h2>Description</h2>
              <div className="description-content">
                {vehicle.description.split('\n\n').map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
              </div>
            </div>

            <div className="vehicle-specs-section">
              <h2>Technical Specifications</h2>
              <VehicleSpecs specs={vehicle.specs} />
            </div>

            {vehicle.features && vehicle.features.length > 0 && (
              <div className="vehicle-features-section">
                <h2>Features &amp; Options</h2>
                <div className="features-list-detailed">
                  {vehicle.features.map((feature, idx) => (
                    <div key={idx} className="feature-tag">
                      ✓ {feature}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <SimilarVehicles currentVehicle={vehicle} onViewDetails={onViewDetails} />
          </div>

          <div className="vehicle-sidebar">
            <div className="sticky-container">
              <div className="price-card">
                <h1 className="vehicle-title-desktop">{vehicle.title}</h1>
                {vehicle.subtitle && (
                  <p className="vehicle-subtitle-desktop">{vehicle.subtitle}</p>
                )}
                <div className="price-display">
                  <div className="price-row">
                    <span className="now-price">£{vehicle.price.toLocaleString()}</span>
                    {vehicle.savings > 0 && (
                      <span className="savings-badge">
                        Save £{vehicle.savings.toLocaleString()}
                      </span>
                    )}
                  </div>
                  {vehicle.wasPrice && (
                    <span className="original-price">
                      Was £{vehicle.wasPrice.toLocaleString()}
                    </span>
                  )}
                </div>

                <div className="key-specs-summary">
                  <div className="spec-pill">{vehicle.specs['Year']}</div>
                  <div className="spec-pill">{vehicle.specs['Mileage']}</div>
                  <div className="spec-pill">{vehicle.specs['Fuel Type']}</div>
                  <div className="spec-pill">{vehicle.specs['Transmission']}</div>
                </div>

                <div className="action-buttons">
                  <button className="btn btn-primary full-width large">
                    Reserve Online - £99
                  </button>
                  <button className="btn btn-secondary full-width">Book Test Drive</button>
                  <button className="btn btn-outline full-width">Request Video Tour</button>
                </div>

                <div className="dealer-info">
                  {siteInfo && (
                    <>
                      <h4>{siteInfo.address.lines[0]}</h4>
                      <address className="dealer-address">
                        {siteInfo.address.lines.slice(1).map((line) => (
                          <span key={line}>{line}</span>
                        ))}
                      </address>
                      <p className="dealer-rating"><IconStar /> 4.9 (128 reviews)</p>
                      <a href={`tel:${siteInfo.phone.tel}`} className="phone-link">
                        <IconPhone /> {siteInfo.phone.display}
                      </a>
                    </>
                  )}
                </div>
              </div>

              <FinanceCalculator price={vehicle.price} />
              <EnquiryForm />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetailsPage;
